import { onCLS, onINP, onLCP, type Metric } from 'web-vitals';
import { getSafeRoutePattern } from './sanitization';
import { getAnonymousSessionId } from './telemetrySession';

type CoreWebVitalName = 'CLS' | 'INP' | 'LCP';

interface CoreWebVitalsPayload {
  metric: {
    name: CoreWebVitalName;
    id: string;
    value: number;
    delta: number;
    rating: Metric['rating'];
  };
  page: {
    route: string;
  };
  app: {
    environment: string;
    gitSha: string;
  };
  session: {
    id: string;
  };
  timestamp: string;
}

// Endpoint for sending web vitals (supports both GCP custom endpoint OR local backend aggregation).
const endpoint =
  import.meta.env.VITE_WEB_VITALS_ENDPOINT?.trim() ||
  '/telemetry/web-vitals';

// Build the payload for sending to web vitals endpoint (HIPAA-safe format).
// Exported for unit testing (mirrors the build*ForTest convention in server.mjs).
export const buildPayload = (metric: Metric): CoreWebVitalsPayload => ({
  metric: {
    name: metric.name as CoreWebVitalName,
    id: metric.id,
    value: metric.value,
    delta: metric.delta,
    rating: metric.rating,
  },
  page: {
    // Use sanitized route (all IDs replaced with :id, no query strings).
    route: getSafeRoutePattern(),
  },
  app: {
    environment:
      import.meta.env.VITE_APP_ENV || import.meta.env.MODE || 'unknown',
    gitSha: import.meta.env.VITE_GIT_SHA || 'local',
  },
  session: {
    // Anonymous session ID with no healthcare data correlation.
    id: getAnonymousSessionId(),
  },
  timestamp: new Date().toISOString(),
});

// Send a single Core Web Vital metric.
const sendMetric = (metric: Metric): void => {
  if (!import.meta.env.PROD) {
    if (import.meta.env.DEV) {
      console.info('[web-vitals]', buildPayload(metric));
    }
    return;
  }

  const body = JSON.stringify(buildPayload(metric));

  // Prefer sendBeacon for reliability (fires even on page unload).
  if (navigator.sendBeacon) {
    const sent = navigator.sendBeacon(
      endpoint,
      new Blob([body], { type: 'application/json' })
    );

    if (sent) {
      return;
    }
  }

  // Fallback to fetch with keepalive.
  void fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
    credentials: 'omit',
  }).catch(() => {
    // Monitoring must never affect application behavior.
  });
};

// Initialize Core Web Vitals tracking (CLS, INP, LCP).
export const initializeCoreWebVitals = (): void => {
  onCLS(sendMetric);
  onINP(sendMetric);
  onLCP(sendMetric);
};
